import { useEffect, useMemo, useState } from 'react';
import {
  AlertOctagon,
  ArrowDown,
  Bot,
  Braces,
  Clock3,
  Copy,
  Cpu,
  Download,
  FileJson,
  Globe2,
  MessageSquareText,
  Radio,
  Server,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import type { AiRoundtripDetail, TimelineViewItem, TraceDetailView, TraceSource } from '../types';
import { formatDuration, formatNumber, formatTime, prettyJson, sourceLabel } from '../lib/format';
import { serializeSanitizedTrace } from '../lib/export';
import { StatusPill } from './StatusPill';
import { TraceModelRequestsPanel } from './TraceModelRequestsPanel';

const sourceIcon: Record<TraceSource, typeof Server> = {
  'ai-backend': Bot,
  'game-server': Server,
  'unity-client': Radio,
  platform: Globe2,
};

interface MovementDiagnostic {
  readonly toolName?: string;
  readonly callId?: string;
  readonly target?: string;
  readonly status?: string;
  readonly reasonCode?: string;
  readonly reason: string;
  readonly backendAccepted?: boolean;
  readonly applied?: boolean;
  readonly terminal?: boolean;
}

function payloadText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

function movementDiagnostic(item: TimelineViewItem): MovementDiagnostic | undefined {
  const { event } = item;
  if (event.source !== 'game-server' || event.type !== 'system.notice' ||
      !['movement_rejected', 'movement_failed'].includes(String(event.payload.code))) return undefined;
  const reason = payloadText(event.payload.reason);
  if (reason === undefined) return undefined;
  const optionalBoolean = (value: unknown): boolean | undefined =>
    typeof value === 'boolean' ? value : undefined;
  const toolName = payloadText(event.payload.toolName);
  const callId = payloadText(event.payload.callId);
  const target = payloadText(event.payload.target);
  const status = payloadText(event.payload.status);
  const reasonCode = payloadText(event.payload.reasonCode);
  const backendAccepted = optionalBoolean(event.payload.backendAccepted);
  const applied = optionalBoolean(event.payload.applied);
  const terminal = optionalBoolean(event.payload.terminal);
  return {
    ...(toolName === undefined ? {} : { toolName }),
    ...(callId === undefined ? {} : { callId }),
    ...(target === undefined ? {} : { target }),
    ...(status === undefined ? {} : { status }),
    ...(reasonCode === undefined ? {} : { reasonCode }),
    reason,
    ...(backendAccepted === undefined ? {} : { backendAccepted }),
    ...(applied === undefined ? {} : { applied }),
    ...(terminal === undefined ? {} : { terminal }),
  };
}

function TimelineItem({ item, last }: { item: TimelineViewItem; last: boolean }) {
  const { event } = item;
  const Icon = sourceIcon[event.source];
  const movement = movementDiagnostic(item);
  return (
    <li className={`timeline-item timeline-item--${item.status}`}>
      <div className="timeline-item__track" aria-hidden="true">
        <span><Icon size={14} /></span>
        {!last && <i />}
      </div>
      <div className="timeline-item__content">
        <div className="timeline-item__topline">
          <div>
            <time>+{formatDuration(item.offsetMs)}</time>
            <span>{sourceLabel(event.source)}</span>
            <code>{event.type}</code>
          </div>
          <StatusPill status={item.status} compact />
        </div>
        <strong>{item.title}</strong>
        {item.summary && <p>{item.summary}</p>}
        {movement && (
          <div className="timeline-movement-diagnostic" role="note" aria-label="移动拒绝诊断">
            <div className="timeline-movement-diagnostic__tags">
              {movement.toolName && <code>tool={movement.toolName}</code>}
              {movement.status && <code>status={movement.status}</code>}
              {movement.target && <code>target={movement.target}</code>}
              {movement.reasonCode && <code>reasonCode={movement.reasonCode}</code>}
              {movement.backendAccepted !== undefined &&
                <code>backendAccepted={String(movement.backendAccepted)}</code>}
              {movement.applied !== undefined && <code>applied={String(movement.applied)}</code>}
              {movement.terminal !== undefined && <code>terminal={String(movement.terminal)}</code>}
            </div>
            <p><strong>拒绝原因</strong><span>{movement.reason}</span></p>
            {movement.callId && <small>call_id={movement.callId}</small>}
          </div>
        )}
        {Object.keys(event.payload).length > 0 && (
          <details className="json-disclosure">
            <summary><Braces size={13} />Metadata</summary>
            <pre>{prettyJson(event.payload)}</pre>
          </details>
        )}
      </div>
    </li>
  );
}

function ExchangeDetail({ exchange }: { exchange: AiRoundtripDetail }) {
  const [copied, setCopied] = useState(false);
  const copyRequest = async () => {
    await navigator.clipboard?.writeText(prettyJson(exchange.request));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <article className="exchange-card">
      <header>
        <div>
          <span className="exchange-card__icon"><Cpu size={16} /></span>
          <div><strong>{exchange.model}</strong><span>{formatTime(exchange.startedAt)}</span></div>
        </div>
        <div className="exchange-card__metrics">
          <span><Clock3 size={13} />{formatDuration(exchange.durationMs)}</span>
          <span>{formatNumber(exchange.tokenUsage.total)} tokens</span>
        </div>
      </header>
      {exchange.errorMessage && <div className="inline-error"><AlertOctagon size={16} />{exchange.errorMessage}</div>}
      <div className="exchange-columns">
        <section>
          <div className="subsection-title">
            <div><FileJson size={15} /><strong>模型请求</strong></div>
            <button className="icon-button" onClick={copyRequest} title="复制模型请求"><Copy size={14} />{copied ? '已复制' : '复制'}</button>
          </div>
          {exchange.request.system && (
            <details className="request-section">
              <summary>System prompt</summary>
              <p>{exchange.request.system}</p>
            </details>
          )}
          <div className="message-stack">
            {exchange.request.messages.map((message, index) => (
              <div className="message" key={`${message.role}-${index}`}>
                <span className="message__identity">
                  <span>{message.role}</span>
                  {message.toolCalls?.map((tool, toolIndex) => (
                    <code
                      className={`prompt-message__tool-name${tool.name === undefined ? ' prompt-message__tool-name--unknown_legacy' : ''}`}
                      key={`${tool.callId ?? 'no-call-id'}-${toolIndex}`}
                      title={tool.callId === undefined ? 'call_id unavailable' : `call_id=${tool.callId}`}
                    >
                      {tool.name ?? 'unknown/legacy'}
                    </code>
                  ))}
                  {message.role === 'tool' && (
                    <code
                      className={`prompt-message__tool-name${message.name === undefined ? ' prompt-message__tool-name--unknown_legacy' : ' prompt-message__tool-name--correlated'}`}
                      title={message.toolCallId === undefined ? 'call_id unavailable' : `call_id=${message.toolCallId}`}
                    >
                      {message.name ?? 'unknown/legacy'}
                    </code>
                  )}
                </span>
                <p>{typeof message.content === 'string' ? message.content : prettyJson(message.content)}</p>
              </div>
            ))}
          </div>
          {!!exchange.request.tools?.length && (
            <details className="request-section">
              <summary>{exchange.request.tools.length} 个可用 Tools</summary>
              <pre>{prettyJson(exchange.request.tools)}</pre>
            </details>
          )}
        </section>
        <section>
          <div className="subsection-title">
            <div><MessageSquareText size={15} /><strong>模型响应</strong></div>
            {exchange.response.finishReason && <code>{exchange.response.finishReason}</code>}
          </div>
          {exchange.response.text ? <blockquote>{exchange.response.text}</blockquote> : <p className="muted">无文本响应</p>}
          {!!exchange.response.toolCalls.length && (
            <div className="tool-call-list">
              <div className="tool-call-list__title"><Wrench size={14} />Tool calls</div>
              {exchange.response.toolCalls.map((tool) => (
                <details className="tool-call" open key={tool.callId}>
                  <summary>
                    <code>{tool.name}</code>
                    <StatusPill status={tool.status} compact />
                  </summary>
                  <div className="tool-call__flow">
                    <div><span>ARGUMENTS</span><pre>{prettyJson(tool.arguments)}</pre></div>
                    {tool.result !== undefined && <ArrowDown size={15} />}
                    {tool.result !== undefined && <div><span>RESULT</span><pre>{prettyJson(tool.result)}</pre></div>}
                  </div>
                </details>
              ))}
            </div>
          )}
        </section>
      </div>
    </article>
  );
}

export function TraceDetailPanel({ detail, busy, error, canExport, canReadAiDetails }: { detail: TraceDetailView | undefined; busy: boolean; error: string | undefined; canExport: boolean; canReadAiDetails: boolean }) {
  const [tab, setTab] = useState<'timeline' | 'http' | 'ai'>(canReadAiDetails ? 'http' : 'timeline');
  const [exportNotice, setExportNotice] = useState<string>();
  const failures = useMemo(() => detail?.timeline.filter((event) => event.status === 'error').length ?? 0, [detail]);
  const redactedJson = useMemo(() => detail ? serializeSanitizedTrace(detail) : '', [detail]);

  useEffect(() => {
    setTab(canReadAiDetails ? 'http' : 'timeline');
  }, [canReadAiDetails, detail?.traceId]);

  const copyExport = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(redactedJson);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = redactedJson;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.append(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      setExportNotice('已复制脱敏 JSON');
    } catch {
      setExportNotice('复制失败，请使用下载');
    }
  };

  const downloadExport = () => {
    if (!detail) return;
    const url = URL.createObjectURL(new Blob([redactedJson], { type: 'application/json;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `maland-trace-${detail.traceId}.redacted.json`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
    setExportNotice('脱敏 JSON 已导出');
  };

  if (busy) {
    return <section className="trace-detail panel trace-detail--loading" aria-label="正在加载详情"><span /><span /><span /></section>;
  }
  if (error) {
    return (
      <section className="trace-detail panel empty-state empty-state--error">
        <AlertOctagon size={27} /><strong>详情加载失败</strong><p>{error}</p>
      </section>
    );
  }
  if (!detail) {
    return <section className="trace-detail panel empty-state"><FileJson size={27} /><strong>选择一条 Trace</strong><p>查看跨服务时间线和完整模型交换。</p></section>;
  }

  return (
    <section className="trace-detail panel" aria-label="Trace 详情">
      <header className="detail-hero">
        <div className="detail-hero__topline">
          <span className="eyebrow">TRACE INSPECTOR</span>
          <div className="detail-hero__actions">
            {canExport && (
              <>
                <button className="button button--ghost button--compact" onClick={() => void copyExport()}><Copy size={13} />复制脱敏 JSON</button>
                <button className="button button--ghost button--compact" onClick={downloadExport}><Download size={13} />导出 JSON</button>
              </>
            )}
            <StatusPill status={detail.status} />
          </div>
        </div>
        {canExport && exportNotice && <div className="export-notice" role="status">{exportNotice}</div>}
        <h2>{detail.headline}</h2>
        <div className="detail-hero__ids"><code>{detail.runId}</code><span>/</span><code>{detail.roomId}</code></div>
        {detail.error && <div className="detail-alert"><AlertOctagon size={18} /><div><strong>检测到异常</strong><p>{detail.error}</p></div></div>}
        <div className="detail-metrics">
          <div><span>总耗时</span><strong>{formatDuration(detail.durationMs)}</strong></div>
          <div><span>Token</span><strong>{formatNumber(detail.tokenUsage?.total)}</strong></div>
          <div><span>时间线事件</span><strong>{detail.timeline.length}</strong></div>
          <div><span>异常事件</span><strong className={failures ? 'danger' : ''}>{failures}</strong></div>
        </div>
        <div className="detail-meta">
          <span>Game <b>{detail.gameVersion ?? '—'}</b></span>
          <span>Prompt <b>{detail.promptVersion ?? '—'}</b></span>
          <span>{formatTime(detail.startedAt)}</span>
        </div>
      </header>
      <div className="tabs" role="tablist" aria-label="详情视图">
        {canReadAiDetails && <button role="tab" aria-selected={tab === 'http'} onClick={() => setTab('http')}>HTTP 发包/回包</button>}
        <button role="tab" aria-selected={tab === 'timeline'} onClick={() => setTab('timeline')}>结构化时间线 <span>{detail.timeline.length}</span></button>
        {canReadAiDetails && <button role="tab" aria-selected={tab === 'ai'} onClick={() => setTab('ai')}>AI 交换 <span>{detail.aiRoundtrips.length}</span></button>}
      </div>
      {!canReadAiDetails && <div className="sensitive-access-notice"><ShieldAlert size={17} /><div><strong>当前账号为 Viewer 权限</strong><span>Prompt、HTTP 发包 Body 和回包 Body 已隐藏。请由管理员将测试账号加入 operator 白名单后重新登录。</span></div></div>}
      {tab === 'http' ? (
        <TraceModelRequestsPanel runId={detail.runId} />
      ) : tab === 'timeline' ? (
        <ol className="timeline">
          {detail.timeline.map((item, index) => <TimelineItem item={item} last={index === detail.timeline.length - 1} key={item.event.eventId} />)}
        </ol>
      ) : detail.aiRoundtrips.length ? (
        <div className="exchange-list">{detail.aiRoundtrips.map((exchange) => <ExchangeDetail exchange={exchange} key={exchange.roundtripId} />)}</div>
      ) : (
        <div className="empty-state"><Bot size={25} /><strong>没有 AI 交换记录</strong><p>这条 Trace 由 {sourceLabel(detail.source)} 采集，暂未关联上游模型调用。</p></div>
      )}
    </section>
  );
}
